import { compareISO, d } from '@easiibill/shared';

export const INVOICE_STATUSES = [
  'draft',
  'sent',
  'viewed',
  'partially_paid',
  'paid',
  'overdue',
  'cancelled',
  'void',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  void: 'Void',
};

export interface StatusFacts {
  issued: boolean;
  viewed: boolean;
  cancelled: boolean;
  voided: boolean;
  total: string;
  amountPaid: string;
  dueDate: string | null;
  /** Today in the business time zone (ISO date). */
  today: string;
}

/** Derives the invoice status from its lifecycle facts. */
export function deriveStatus(facts: StatusFacts): InvoiceStatus {
  if (facts.voided) return 'void';
  if (facts.cancelled) return 'cancelled';
  if (!facts.issued) return 'draft';
  const total = d(facts.total);
  const paid = d(facts.amountPaid);
  if (paid.gte(total) && (total.gt(0) || paid.gt(0))) return 'paid';
  if (total.eq(0)) return 'paid';
  if (facts.dueDate && compareISO(facts.dueDate, facts.today) < 0) return 'overdue';
  if (paid.gt(0)) return 'partially_paid';
  return facts.viewed ? 'viewed' : 'sent';
}

export type InvoiceAction =
  | 'edit'
  | 'delete'
  | 'send'
  | 'markSent'
  | 'recordPayment'
  | 'cancel'
  | 'void'
  | 'duplicate'
  | 'share'
  | 'download';

/** Actions available for an invoice in a given state (enforced by the API). */
export function allowedActions(status: InvoiceStatus, amountPaid: string): Set<InvoiceAction> {
  const actions = new Set<InvoiceAction>(['duplicate', 'download']);
  const hasPayments = d(amountPaid).gt(0);
  switch (status) {
    case 'draft':
      ['edit', 'delete', 'send', 'markSent', 'recordPayment', 'share'].forEach((a) =>
        actions.add(a as InvoiceAction),
      );
      break;
    case 'sent':
    case 'viewed':
    case 'overdue':
    case 'partially_paid':
      ['edit', 'send', 'recordPayment', 'share', 'void'].forEach((a) => actions.add(a as InvoiceAction));
      if (!hasPayments) actions.add('cancel');
      break;
    case 'paid':
      ['send', 'share', 'void'].forEach((a) => actions.add(a as InvoiceAction));
      break;
    case 'cancelled':
    case 'void':
      break;
  }
  return actions;
}

export function isOpenStatus(status: InvoiceStatus): boolean {
  return status === 'sent' || status === 'viewed' || status === 'overdue' || status === 'partially_paid';
}
